import { apiHelpers } from '@/services/api';
import type {
  BirthdayGiftCoordination,
  BirthdayGiftSuggestion,
  CreateSuggestionDto,
  MarkPurchasedDto,
  GiftCoordinationFilters,
  SuggestionsResponse,
} from '@/types/birthdayGift.types';

// Birthday Gift Coordination Service
export const birthdayGiftService = {
  /**
   * Get or create gift coordination for a birthday
   * @param birthdayId Birthday ID
   */
  async getCoordination(birthdayId: string): Promise<BirthdayGiftCoordination> {
    return apiHelpers.get<BirthdayGiftCoordination>(`/birthdays/${birthdayId}/gifts`);
  },

  /**
   * Get all suggestions for a birthday gift coordination
   * @param birthdayId Birthday ID
   * @param filters Optional filters for pagination and sorting
   */
  async getSuggestions(
    birthdayId: string,
    filters?: GiftCoordinationFilters
  ): Promise<SuggestionsResponse> {
    return apiHelpers.get<SuggestionsResponse>(
      `/birthdays/${birthdayId}/gifts/suggestions`,
      filters as Record<string, unknown> | undefined
    );
  },

  /**
   * Create a new gift suggestion
   * @param birthdayId Birthday ID
   * @param data Suggestion data
   */
  async createSuggestion(
    birthdayId: string,
    data: CreateSuggestionDto
  ): Promise<BirthdayGiftSuggestion> {
    return apiHelpers.post<BirthdayGiftSuggestion>(
      `/birthdays/${birthdayId}/gifts/suggestions`,
      data
    );
  },

  /**
   * Delete a gift suggestion (only creator)
   * @param birthdayId Birthday ID
   * @param suggestionId Suggestion ID
   */
  async deleteSuggestion(birthdayId: string, suggestionId: string): Promise<void> {
    return apiHelpers.delete(`/birthdays/${birthdayId}/gifts/suggestions/${suggestionId}`);
  },

  /**
   * Vote on a gift suggestion
   * @param birthdayId Birthday ID
   * @param suggestionId Suggestion ID
   */
  async vote(birthdayId: string, suggestionId: string): Promise<BirthdayGiftSuggestion> {
    return apiHelpers.post<BirthdayGiftSuggestion>(
      `/birthdays/${birthdayId}/gifts/suggestions/${suggestionId}/vote`
    );
  },

  /**
   * Remove vote from a gift suggestion
   * @param birthdayId Birthday ID
   * @param suggestionId Suggestion ID
   */
  async removeVote(birthdayId: string, suggestionId: string): Promise<BirthdayGiftSuggestion> {
    return apiHelpers.delete<BirthdayGiftSuggestion>(
      `/birthdays/${birthdayId}/gifts/suggestions/${suggestionId}/vote`
    );
  },

  /**
   * Claim the gift for purchase
   * @param birthdayId Birthday ID
   */
  async claimGift(birthdayId: string): Promise<BirthdayGiftCoordination> {
    return apiHelpers.post<BirthdayGiftCoordination>(`/birthdays/${birthdayId}/gifts/claim`);
  },

  /**
   * Unclaim the gift
   * @param birthdayId Birthday ID
   */
  async unclaimGift(birthdayId: string): Promise<BirthdayGiftCoordination> {
    return apiHelpers.delete<BirthdayGiftCoordination>(`/birthdays/${birthdayId}/gifts/claim`);
  },

  /**
   * Mark the gift as purchased and create expense
   * @param birthdayId Birthday ID
   * @param data Purchase data including expense amount and split
   */
  async markPurchased(
    birthdayId: string,
    data: MarkPurchasedDto
  ): Promise<BirthdayGiftCoordination> {
    return apiHelpers.post<BirthdayGiftCoordination>(
      `/birthdays/${birthdayId}/gifts/purchased`,
      data
    );
  },

  /**
   * Get all upcoming birthday gift coordinations
   * @param days Number of days to look ahead (default: 60)
   */
  async getUpcomingCoordinations(days: number = 60): Promise<BirthdayGiftCoordination[]> {
    const response = await apiHelpers.get<{ coordinations: BirthdayGiftCoordination[] }>(
      '/birthdays/gifts/upcoming',
      { days }
    );
    return response.coordinations ?? [];
  },
};

export default birthdayGiftService;
